import {useCallback, useEffect, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {useUserFeatures} from '../../../../context/user-features-context'
import {applyProductRulesBeforeLoading} from '../../../../features/CatalogGrid/utils/productPreloadRules'
import {useAuthClient} from '../../../../utils/use-auth-client'
import {selectEffectiveRole} from '../../../../app-store/selector'
import {transformProducts} from '../../../../features/CatalogGrid/utils/transformProducts'
import {useCatalogCart} from '../../../../features/CatalogGrid/hooks/useCatalogCart'
import {filterCentersAndAppsByCountry} from '../kp-11Link-utility/kp11-country-filter'
import {KP11LinkCouponService} from '../kp-11Link-utility/kp11-coupon-service'
import {useManyLinkPaymentPreview} from './useManyLinkPaymentPreview'

// Default coupon every non-influencer 1:Many link carries (waives the
// onboarding fee) — same one the legacy page validated and pre-applied.
const OB_FEE_WAIVER_CODE = 'keap-partner-waive-obfee'

// Items every 1:Many link carries, mirroring the legacy cart: the base My
// Account plan and the marketplace subscription with its two free entitlements.
// Flagged alreadyIncluded so the shared summary renders them as "Included".
export const MY_ACCOUNT_CODE = 'cc_basicpls-5-thryv-m2m'
export const MARKETPLACE_CODE = 'appmarket-5-thryv-m2m'

// A 1:Many link is sold to customers who do not exist yet, so details that
// belong to one specific customer — their website domain, EAID, Keap free-trial
// answers — are meaningless up front. The legacy page never asked them (it sent
// mcDomain: ''), and FULSAS-14548 requires the same for Partners and
// Influencers. The catalog defines them for 1:1, so strip just those fields
// here instead of changing the shared panels.
const CUSTOMER_ONLY_FIELD_KEYS = [
  'domain',
  'eaid',
  'keapfreetrial',
  'keapfreetrialappid',
]
export const relaxCustomerFields = products =>
  (products ?? []).map(product =>
    product.fields?.length
      ? {
          ...product,
          fields: product.fields.filter(
            field =>
              !CUSTOMER_ONLY_FIELD_KEYS.includes(
                String(field.fieldKey).toLowerCase(),
              ),
          ),
        }
      : product,
  )

const buildBaselineItems = () => [
  {
    productCode: MY_ACCOUNT_CODE,
    productName: 'My Account',
    productType: 'CENTER',
    unitPrice: 0,
    quantity: 1,
    billingFrequency: 'monthly',
    alreadyIncluded: true,
    addons: [],
  },
  {
    productCode: MARKETPLACE_CODE,
    productName: 'Thryv Add-Ons',
    productType: 'APP',
    unitPrice: 0,
    quantity: 1,
    billingFrequency: 'monthly',
    alreadyIncluded: true,
    addons: [
      {
        productCode: 'addon-5-signatures_usage',
        productName: 'Signatures Usage',
        productType: 'APP',
        quantity: 1,
        unitPrice: 0,
        alreadyIncluded: true,
      },
      {
        productCode: 'app-websitebuilder',
        productName: 'Website Builder',
        productType: 'APP',
        quantity: 1,
        unitPrice: 0,
        alreadyIncluded: true,
      },
    ],
  },
]

// Data layer for the 1:Many catalog page, built on the same shared pieces the
// 1:1 Purchase Link and Proposal Tool use: transformProducts → CatalogGrid
// groups, and useCatalogCart for every add/update/remove/fee rule. Only the
// source differs — the Keap-partner `kp1tomany` inventory, fetched with the
// same app-type rules as the legacy 1:Many page (influencers get their own
// catalog) and filtered with the legacy per-country currency filter.
export function useManyLinkCatalog({selectedCountry}) {
  const client = useAuthClient()
  const roles = useSelector(selectEffectiveRole)
  const isInfluencerOps =
    Array.isArray(roles) && roles.includes('InfluencerOps')
  const userFeatures = useUserFeatures()

  const [productConfigInfo, setProductConfigInfo] = useState({})
  const [items, setItems] = useState(buildBaselineItems)
  const [coupons, setCoupons] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setProductConfigInfo({})
    setItems(buildBaselineItems())
    setCoupons([])

    const catalogRequest = client(
      `plans/5/v1/list?country=${encodeURIComponent(selectedCountry)}`,
      {
        headers: {'app-type': isInfluencerOps ? 'influencerops' : 'kp1tomany'},
      },
    )
    // Resolved before the summary mounts so the shared CouponSearch starts with
    // the waiver already in its list (it only reads its initial coupons once).
    const waiverRequest = isInfluencerOps
      ? Promise.resolve(null)
      : KP11LinkCouponService.validateObFeeCoupon(
          client,
          OB_FEE_WAIVER_CODE,
          selectedCountry,
        ).catch(() => null)

    Promise.all([catalogRequest, waiverRequest])
      .then(([catalog, waiver]) => {
        if (cancelled) return
        const ruleFiltered = applyProductRulesBeforeLoading(
          catalog,
          userFeatures,
        )
        const filtered = filterCentersAndAppsByCountry(
          ruleFiltered,
          selectedCountry,
        )
        setProductConfigInfo({
          ...ruleFiltered,
          centers: relaxCustomerFields(filtered.centers),
          apps: relaxCustomerFields(filtered.apps),
          oneTimeFees: relaxCustomerFields(filtered.oneTimeFees),
          catalogSource: 'keap-partner',
        })
        // The waiver is tied to the onboarding-fee items, and the shared
        // CouponSearch drops coupons whose items are not in the cart — but
        // the legacy page showed it from the start. Also tying it to the
        // always-present base item keeps it listed without changing what it
        // discounts (percent coupon, only the fee items carry a price).
        setCoupons(
          waiver?.isValid
            ? [
                {
                  ...waiver,
                  appliesToItems: [
                    ...(waiver.appliesToItems ?? []),
                    MY_ACCOUNT_CODE,
                  ],
                },
              ]
            : [],
        )
      })
      .catch(err => {
        if (!cancelled) setError(err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [client, selectedCountry, isInfluencerOps, userFeatures])

  const groups = useMemo(
    () => transformProducts(productConfigInfo),
    [productConfigInfo],
  )
  const rawSoftwareGroups = useMemo(
    () => groups.filter(group => group.prod_type === 'Center'),
    [groups],
  )
  const rawAddonGroups = useMemo(
    () =>
      groups.filter(
        group => group.prod_type === 'App' || group.prod_type === 'OneTimeFees',
      ),
    [groups],
  )
  const isDataLoaded =
    'centers' in productConfigInfo || 'apps' in productConfigInfo

  const activeItem = useMemo(
    () => ({items, coupons, isLoaded: true, dueTodayIncludesTax: false}),
    [items, coupons],
  )
  // Shared OrderSummaryContainer writes coupon changes back through this.
  const updateActiveItem = useCallback((_id, changes) => {
    if (changes.coupons) setCoupons(changes.coupons)
    if (changes.items) setItems(changes.items)
  }, [])
  const {refreshPaymentPreview} = useManyLinkPaymentPreview({
    items,
    coupons,
    setItems,
  })

  const cart = useCatalogCart({
    activeItem,
    country: selectedCountry,
    recurlyAccountInfo: null,
    rawAccountItems: [],
    rawSoftwareGroups,
    rawAddonGroups,
    isDataLoaded,
    oneTimeFeeDefinitions: productConfigInfo.oneTimeFees ?? [],
    productConfigInfo,
    clientType: 'NEW',
    onItemsChange: setItems,
  })

  return {
    ...cart,
    items,
    coupons,
    activeItem,
    updateActiveItem,
    refreshPaymentPreview,
    productConfigInfo,
    loading,
    error,
    isInfluencerOps,
  }
}
